// pages/CloudPlatformPage.js - 云平台对接监控
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Tag, Tabs, Button, Badge, message } from 'antd';
import { CloudOutlined, DashboardOutlined, AppstoreOutlined, ThunderboltOutlined, AlertOutlined, SettingOutlined, HistoryOutlined, ReloadOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import moment from 'moment';
import TelemetryTab from './CloudPlatform/components/TelemetryTab';
import { ControllersTab, SubDevicesTab, AlarmTab, TelecontrolTab, LogsTab, HistoryTab } from './CloudPlatform/components/CloudPage';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', text: '#ffffff', muted: 'rgba(255,255,255,0.6)', solar: '#f4c542', storage: '#52e8b0', load: '#ff5a7f', grid: '#3a8dde' };
const DEFAULT_POLL_INTERVAL = 5000;

const CloudPlatformPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('telemetry');
  const [pollInterval, setPollInterval] = useState(DEFAULT_POLL_INTERVAL);
  const [loading, setLoading] = useState(false);
  const [alarmPageSize, setAlarmPageSize] = useState(10);
  const [lastUpdate, setLastUpdate] = useState(null);

  // 当前用户信息
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });

  // 统计数据
  const [controllerStats, setControllerStats] = useState({ total: 0, online: 0, offline: 0, fault: 0 });
  const [subDeviceStats, setSubDeviceStats] = useState([]);
  const [alarmStats, setAlarmStats] = useState({ total: 0, pending: 0, processed: 0, today_count: 0 });

  // 列表数据
  const [controllers, setControllers] = useState([]);
  const [subDevices, setSubDevices] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [alarmsWithDef, setAlarmsWithDef] = useState([]);
  const [controlLogs, setControlLogs] = useState([]);
  const [controllerData, setControllerData] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // LZ4 压缩开关（共享给TelecontrolTab）
  const [lz4Compress, setLz4Compress] = useState(false);

  // 轮询设备响应状态
  const waitForDeviceResponse = async (lcSn, funcId, seq, sentTime, maxRetries = 10, interval = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      await new Promise(r => setTimeout(r, interval));
      try {
        const res = await api.getControlResponseStatus({ lc_sn: lcSn, func_id: funcId, seq, sent_time: sentTime });
        if (res.success && res.data?.hasResponse) {
          return res.data;
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }
    return null;
  };

  // 遥测数据
  const [telemetry, setTelemetry] = useState({
    controller: [], meter: [], bms: [], pcs: [], aircond: [], env: [], pv: [], cell: []
  });
  const [dashboardData, setDashboardData] = useState([]);

  const pageSize = 10;

  // 获取所有遥测数据
  const fetchTelemetry = async () => {
    try {
      const [json1, json2] = await Promise.all([
        api.getTelemetryAllLatest(),
        api.getTelemetryDashboardLatest()
      ]);
      if (json1.success) setTelemetry(json1.data);
      if (json2.success) setDashboardData(json2.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('[Telemetry] Fetch error:', err);
    }
  };

  // 获取控制器统计
  const fetchControllerStats = async () => {
    try {
      const json = await api.getDeviceInfoStats();
      if (json.success) setControllerStats(json.data);
    } catch (err) { console.error(err); }
  };

  // 获取子设备统计
  const fetchSubDeviceStats = async () => {
    try {
      const json = await api.getSubDeviceStatsByType();
      if (json.success) setSubDeviceStats(json.data || []);
    } catch (err) { console.error(err); }
  };

  // 获取子设备列表
  const fetchSubDevices = async (page = 1) => {
    setLoading(true);
    try {
      const json = await api.getSubDeviceList({ page, pageSize });
      if (json.success) setSubDevices(json.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // 获取控制器列表
  const fetchControllers = async (page = 1) => {
    setLoading(true);
    try {
      const json = await api.getDeviceInfoList({ page, pageSize });
      if (json.success) setControllers(json.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // 获取告警
  const fetchAlarms = async (page = 1) => {
    const pageNum = page || 1;
    try {
      const [statsJson, listJson] = await Promise.all([
        api.getAlarmRecordStats(),
        api.getAlarmRecordList({ page: pageNum, pageSize: alarmPageSize })
      ]);
      if (statsJson.success) setAlarmStats(statsJson.data);
      if (listJson.success) setAlarms(listJson.data || []);
    } catch (err) { console.error(err); }
  };

  // 获取告警记录+定义
  const fetchAlarmsWithDef = async () => {
    try {
      const json = await api.getAlarmRecordWithDefinition({ pageSize: alarmPageSize });
      if (json.success) setAlarmsWithDef(json.data || []);
    } catch (err) { console.error(err); }
  };

  // 修改告警状态
  const handleAlarmStatusChange = async (id, status) => {
    try {
      await api.handleAlarmRecord(id, { status });
      message.success(t('common.success'));
      fetchAlarms();
    } catch (err) {
      message.error(t('common.error') + ': ' + err.message);
    }
  };

  // 获取控制器参数数据
  const fetchControllerData = async () => {
    try {
      const json = await api.getControllerDataList();
      if (json.success) setControllerData(json.data || []);
    } catch (err) { console.error(err); }
  };

  // 获取控制日志
  const fetchControlLogs = async (page = 1) => {
    setLoading(true);
    try {
      const json = await api.getControlLogList({ page, pageSize });
      if (json.success) setControlLogs(json.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // 初始化
  useEffect(() => {
    fetchTelemetry();
    fetchControllerStats();
    fetchSubDeviceStats();
    fetchSubDevices();
    fetchAlarms();
    fetchControlLogs();
    fetchControllerData();
  }, []);

  // 轮询遥测数据
  useEffect(() => {
    fetchTelemetry();
    const timer = setInterval(fetchTelemetry, pollInterval);
    return () => clearInterval(timer);
  }, [pollInterval]);

  // 轮询告警数据
  useEffect(() => {
    fetchAlarms();
    const timer = setInterval(() => { fetchAlarms(); fetchAlarmsWithDef(); }, pollInterval);
    return () => clearInterval(timer);
  }, [pollInterval, alarmPageSize]);

  // 轮询告警定义关联数据
  useEffect(() => {
    fetchAlarmsWithDef();
    const timer = setInterval(() => fetchAlarmsWithDef(), pollInterval);
    return () => clearInterval(timer);
  }, [pollInterval, alarmPageSize]);

  // Tab切换
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    switch (activeTab) {
      case 'controllers': fetchControllers(); break;
      case 'sub-devices': fetchSubDevices(); fetchSubDeviceStats(); break;
      case 'telemetry': fetchTelemetry(); break;
      case 'alarms': fetchAlarms(); fetchAlarmsWithDef(); break;
      case 'config': fetchControllers(); fetchControllerData(); break;
      case 'history': fetchControllers(); break;
      case 'logs': fetchControlLogs(); break;
      default: break;
    }
  }, [activeTab]);

  const handleRefresh = async () => {
    try {
      await fetchTelemetry();
      fetchControllers();
      fetchSubDevices();
      fetchSubDeviceStats();
      fetchAlarms();
      fetchControlLogs();
      fetchControllerData();
      setRefreshKey(k => k + 1);
      message.success(t('cloud.refreshSuccess'));
    } catch (err) {
      message.error(t('cloud.refreshFailed') + err.message);
    }
  };

  // 表格列
  const statusColor = { 0: 'default', 1: 'green', 2: 'red' };
  const statusText = { 0: t('status.offline'), 1: t('status.online'), 2: t('status.fault') };
  const severityColor = { 0: 'default', 1: 'blue', 2: 'green', 3: 'orange', 4: 'red' };
  const severityText = { 0: '0', 1: '1', 2: '2', 3: '3', 4: '4' };
  const alarmStatusColor = { 0: 'orange', 1: 'green', 2: 'default' };
  const alarmStatusText = { 0: t('status.pending'), 1: t('status.processed'), 2: t('status.ignored') };
  const DEVICE_TYPE_TEXT = { METER: t('device.meter'), PCS: t('device.pcs'), BMS: t('device.bms'), LIQUID_COOL: t('device.liquid_cool'), HUMITURE: t('device.humiture'), DIDO: t('device.dido') };

  const fmt0 = v => (v == null || v === '' || isNaN(v)) ? '0' : (typeof v === 'number' ? v.toFixed(2) : v);
  const fmtTime = t => t ? new Date(t).toLocaleString() : '-';
  const fmtDate = t => t ? String(t).split('T')[0] : '-';

  // ========== 遥测表格定义 ==========
  const telemetryColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.stationName')}</span>, dataIndex: 'station_name', key: 'station_name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.status')}</span>, dataIndex: 'sys_status', key: 'sys_status', render: v => <Tag color={statusColor[v]}>{statusText[v] || t('cloud.unknown')}</Tag>, width: 100, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.gridPowerKw')}</span>, dataIndex: 'grid_power', key: 'grid_power', render: v => <span style={{ color: COLORS.grid }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.loadPowerKw')}</span>, dataIndex: 'load_power', key: 'load_power', render: v => <span style={{ color: COLORS.load }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.lcPowerKw')}</span>, dataIndex: 'lc_ctrl_power', key: 'lc_ctrl_power', render: v => <span style={{ color: COLORS.solar }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.ratedPowerKw')}</span>, dataIndex: 'cab_power', key: 'cab_power', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.ratedCapacityKwh')}</span>, dataIndex: 'cab_capacity', key: 'cab_capacity', render: fmt0, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const meterColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'global_no', key: 'global_no', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.name')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'online', key: 'online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('status.online') : t('status.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.activePowerKw')}</span>, dataIndex: 'active_power_kw', key: 'active_power_kw', render: v => <span style={{ color: COLORS.solar }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.reactivePowerKvar')}</span>, dataIndex: 'reactive_power_kvar', key: 'reactive_power_kvar', render: v => <span style={{ color: COLORS.grid }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.apparentPowerKva')}</span>, dataIndex: 'apparent_power_kva', key: 'apparent_power_kva', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseVoltage')}</span>, dataIndex: 'voltage_a_v', key: 'voltage_a_v', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseVoltage')}</span>, dataIndex: 'voltage_b_v', key: 'voltage_b_v', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseVoltage')}</span>, dataIndex: 'voltage_c_v', key: 'voltage_c_v', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseCurrent')}</span>, dataIndex: 'current_a_a', key: 'current_a_a', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseCurrent')}</span>, dataIndex: 'current_b_a', key: 'current_b_a', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseCurrent')}</span>, dataIndex: 'current_c_a', key: 'current_c_a', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.powerFactor')}</span>, dataIndex: 'power_factor', key: 'power_factor', render: v => fmt0(v), width: 110 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.frequencyHz')}</span>, dataIndex: 'frequency_hz', key: 'frequency_hz', render: v => fmt0(v), width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const meterExpandable = {
    expandedRowRender: (record) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.phaseActivePowerKw')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.phaseA')}: {fmt0(record.AphaseActivePower)}</Tag>
              <Tag>{t('cloud.phaseB')}: {fmt0(record.BphaseActivePower)}</Tag>
              <Tag>{t('cloud.phaseC')}: {fmt0(record.CphaseActivePower)}</Tag>
            </div>
          </div>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.phaseApparentPowerKva')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.phaseA')}: {fmt0(record.AphaseApparentPower)}</Tag>
              <Tag>{t('cloud.phaseB')}: {fmt0(record.BphaseApparentPower)}</Tag>
              <Tag>{t('cloud.phaseC')}: {fmt0(record.CphaseApparentPower)}</Tag>
            </div>
          </div>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.phasePowerFactor')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.phaseA')}: {fmt0(record.AphasePowerFactor)}</Tag>
              <Tag>{t('cloud.phaseB')}: {fmt0(record.BphasePowerFactor)}</Tag>
              <Tag>{t('cloud.phaseC')}: {fmt0(record.CphasePowerFactor)}</Tag>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.positiveActiveEnergyKwh')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.rate')}1: {fmt0(record.PositiveActiveRate1)}</Tag>
              <Tag>{t('cloud.rate')}2: {fmt0(record.PositiveActiveRate2)}</Tag>
              <Tag>{t('cloud.rate')}3: {fmt0(record.PositiveActiveRate3)}</Tag>
              <Tag>{t('cloud.rate')}4: {fmt0(record.PositiveActiveRate4)}</Tag>
            </div>
          </div>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.reverseActiveEnergyKwh')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.rate')}1: {fmt0(record.ReverseActiveRate1)}</Tag>
              <Tag>{t('cloud.rate')}2: {fmt0(record.ReverseActiveRate2)}</Tag>
              <Tag>{t('cloud.rate')}3: {fmt0(record.ReverseActiveRate3)}</Tag>
              <Tag>{t('cloud.rate')}4: {fmt0(record.ReverseActiveRate4)}</Tag>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.positiveReactiveEnergyKvarh')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.total')}: {fmt0(record.PositiveReactiveEnergy)}</Tag>
              <Tag>{t('cloud.rate')}1: {fmt0(record.PositiveReactiveRate1)}</Tag>
              <Tag>{t('cloud.rate')}2: {fmt0(record.PositiveReactiveRate2)}</Tag>
              <Tag>{t('cloud.rate')}3: {fmt0(record.PositiveReactiveRate3)}</Tag>
              <Tag>{t('cloud.rate')}4: {fmt0(record.PositiveReactiveRate4)}</Tag>
            </div>
          </div>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.reverseReactiveEnergyKvarh')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag>{t('cloud.total')}: {fmt0(record.ReverseReactiveEnergy)}</Tag>
              <Tag>{t('cloud.rate')}1: {fmt0(record.ReverseReactiveRate1)}</Tag>
              <Tag>{t('cloud.rate')}2: {fmt0(record.ReverseReactiveRate2)}</Tag>
              <Tag>{t('cloud.rate')}3: {fmt0(record.ReverseReactiveRate3)}</Tag>
              <Tag>{t('cloud.rate')}4: {fmt0(record.ReverseReactiveRate4)}</Tag>
            </div>
          </div>
        </div>
      </div>
    ),
  };

  const bmsColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'global_no', key: 'global_no', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.name')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'online', key: 'online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('status.online') : t('status.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.soc')}</span>, dataIndex: 'soc', key: 'soc', render: v => <span style={{ color: COLORS.storage, fontWeight: 'bold' }}>{fmt0(v)}</span>, width: 90 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.soh')}</span>, dataIndex: 'soh', key: 'soh', render: fmt0, width: 90 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.rackVoltageV')}</span>, dataIndex: 'voltage_v', key: 'voltage_v', render: fmt0, width: 150 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.rackCurrentA')}</span>, dataIndex: 'current_a', key: 'current_a', render: fmt0, width: 150 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.runStatus')}</span>, dataIndex: 'run_status', key: 'run_status', render: v => v != null ? <Tag color={v === 1 ? 'green' : 'default'}>{v}</Tag> : '-', width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.minTempC')}</span>, dataIndex: 'min_temperature_c', key: 'min_temperature_c', render: fmt0, width: 130 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.maxTempC')}</span>, dataIndex: 'max_temperature_c', key: 'max_temperature_c', render: fmt0, width: 130 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.avgTempC')}</span>, dataIndex: 'avg_temperature_c', key: 'avg_temperature_c', render: fmt0, width: 140 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cellAvgVoltMv')}</span>, dataIndex: 'avg_cell_voltage_v', key: 'avg_cell_voltage_v', render: v => fmt0(v * 1000), width: 160 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cellMinVoltMv')}</span>, dataIndex: 'min_cell_voltage_v', key: 'min_cell_voltage_v', render: v => fmt0(v * 1000), width: 160 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cellMaxVoltMv')}</span>, dataIndex: 'max_cell_voltage_v', key: 'max_cell_voltage_v', render: v => fmt0(v * 1000), width: 160 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  // BMS展开行 - 显示单体电压和温度数组
  const bmsExpandable = {
    expandedRowRender: (record) => {
      const cellVol = record.CellVol ? (typeof record.CellVol === 'string' ? JSON.parse(record.CellVol) : record.CellVol) : [];
      const cellTemp = record.CellTemp ? (typeof record.CellTemp === 'string' ? JSON.parse(record.CellTemp) : record.CellTemp) : [];
      return (
        <div style={{ display: 'flex', gap: 24 }}>
          {cellVol.length > 0 && (
            <div>
              <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.cellVoltageMv')}</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 600 }}>
                {cellVol.map((v, i) => (
                  <span key={i} style={{ background: 'rgba(0,180,216,0.1)', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>
                    {i + 1}: {v}
                  </span>
                ))}
              </div>
            </div>
          )}
          {cellTemp.length > 0 && (
            <div>
              <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.tempSamplingPointC')}</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 600 }}>
                {cellTemp.map((v, i) => (
                  <span key={i} style={{ background: 'rgba(82,232,176,0.1)', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>
                    {i + 1}: {v}{t('cloud.celsius')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
  };

  const pcsColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'global_no', key: 'global_no', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.name')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'online', key: 'online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('status.online') : t('status.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.activePowerKw')}</span>, dataIndex: 'active_power_kw', key: 'active_power_kw', render: v => <span style={{ color: COLORS.solar, fontWeight: 'bold' }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.reactivePowerKvar')}</span>, dataIndex: 'reactive_power_kvar', key: 'reactive_power_kvar', render: fmt0, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.apparentPowerKva')}</span>, dataIndex: 'apparent_power_kva', key: 'apparent_power_kva', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseVoltage')}</span>, dataIndex: 'ac_voltage_ab_v', key: 'ac_voltage_ab_v', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseVoltage')}</span>, dataIndex: 'ac_voltage_bc_v', key: 'ac_voltage_bc_v', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseVoltage')}</span>, dataIndex: 'ac_voltage_ca_v', key: 'ac_voltage_ca_v', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseCurrent')}</span>, dataIndex: 'ac_current_a_a', key: 'ac_current_a_a', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseCurrent')}</span>, dataIndex: 'ac_current_b_a', key: 'ac_current_b_a', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseCurrent')}</span>, dataIndex: 'ac_current_c_a', key: 'ac_current_c_a', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.frequencyHz')}</span>, dataIndex: 'frequency_hz', key: 'frequency_hz', render: fmt0, width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.runStatus')}</span>, dataIndex: 'run_status', key: 'run_status', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v}</Tag>, width: 90 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const pcsExpandable = {
    expandedRowRender: (record) => (
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.phaseApparentPowerKva')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Tag>{t('cloud.phaseA')}: {fmt0(record.AphaseApparentPower)}</Tag>
            <Tag>{t('cloud.phaseB')}: {fmt0(record.BphaseApparentPower)}</Tag>
            <Tag>{t('cloud.phaseC')}: {fmt0(record.CphaseApparentPower)}</Tag>
          </div>
        </div>
        <div>
          <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.rawTags')}</div>
          <code style={{ fontSize: 11, color: COLORS.muted }}>{record.tags_json || '-'}</code>
        </div>
      </div>
    ),
  };

  const liquidCoolColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'global_no', key: 'global_no', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.name')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'online', key: 'online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('status.online') : t('status.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.runStatus')}</span>, dataIndex: 'run_status', key: 'run_status', render: v => v != null ? <Tag color={v === 1 ? 'green' : 'default'}>{v}</Tag> : '-', width: 90 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.mode')}</span>, dataIndex: 'mode', key: 'mode', render: fmt0, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.temperatureC')}</span>, dataIndex: 'temperature_c', key: 'temperature_c', render: v => <span style={{ color: COLORS.solar }}>{fmt0(v)}</span>, width: 140, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.setTemperatureC')}</span>, dataIndex: 'set_temperature_c', key: 'set_temperature_c', render: fmt0, width: 140, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.inletTempC')}</span>, dataIndex: 'inlet_temperature_c', key: 'inlet_temperature_c', render: fmt0, width: 140, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const humitureColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'global_no', key: 'global_no', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.name')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'online', key: 'online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('status.online') : t('status.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.temperatureC')}</span>, dataIndex: 'temperature_c', key: 'temperature_c', render: v => <span style={{ color: COLORS.solar, fontWeight: 'bold' }}>{fmt0(v)}</span>, width: 140, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.humidity')}</span>, dataIndex: 'humidity_percent', key: 'humidity_percent', render: v => <span style={{ color: COLORS.grid, fontWeight: 'bold' }}>{fmt0(v)}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const didoColumns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'global_no', key: 'global_no', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 110, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.name')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'online', key: 'online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('status.online') : t('status.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const didoExpandable = {
    expandedRowRender: (record) => {
      const tags = record.tags_json ? (typeof record.tags_json === 'string' ? JSON.parse(record.tags_json) : record.tags_json) : {};
      return (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.didoDetail')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Tag color={tags.DI === 1 ? 'green' : 'default'}>DI: {tags.DI === 1 ? t('cloud.closed') : t('cloud.open')}</Tag>
              <Tag color={tags.DO === 1 ? 'green' : 'default'}>DO: {tags.DO === 1 ? t('cloud.closed') : t('cloud.open')}</Tag>
            </div>
          </div>
          <div>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.rawTags')}</div>
            <code style={{ fontSize: 11, color: COLORS.muted }}>{record.tags_json || '-'}</code>
          </div>
        </div>
      );
    },
  };

  // Dashboard表格
  const dashboardMeterColumns = [
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceId')}</span>, dataIndex: 'device_info_id', key: 'device_info_id', width: 130 },
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.no')}</span>, dataIndex: 'no', key: 'no', render: t => <span style={{ color: COLORS.primary }}>{t}</span>, width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'Online', key: 'Online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('cloud.online') : t('cloud.offline')}</Tag>, width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmCnt')}</span>, dataIndex: 'AlarmCnt', key: 'AlarmCnt', render: fmt0, width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseVoltage')}</span>, dataIndex: 'AphaseVoltage', key: 'AphaseVoltage', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseVoltage')}</span>, dataIndex: 'BphaseVoltage', key: 'BphaseVoltage', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseVoltage')}</span>, dataIndex: 'CphaseVoltage', key: 'CphaseVoltage', render: v => <span style={{ color: COLORS.muted }}>{fmt0(v)}</span>, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseCurrent')}</span>, dataIndex: 'AphaseCurrent', key: 'AphaseCurrent', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseCurrent')}</span>, dataIndex: 'BphaseCurrent', key: 'BphaseCurrent', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseCurrent')}</span>, dataIndex: 'CphaseCurrent', key: 'CphaseCurrent', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.activePowerKw')}</span>, dataIndex: 'ActivePower', key: 'ActivePower', render: v => <span style={{ color: COLORS.solar }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.reactivePowerKvar')}</span>, dataIndex: 'ReactivePower', key: 'ReactivePower', render: fmt0, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const dashboardPcsColumns = [
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceId')}</span>, dataIndex: 'device_info_id', key: 'device_info_id', width: 130 },
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'Online', key: 'Online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('cloud.online') : t('cloud.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseVoltage')}</span>, dataIndex: 'AphaseVoltage', key: 'AphaseVoltage', render: v => fmt0(v), width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseVoltage')}</span>, dataIndex: 'BphaseVoltage', key: 'BphaseVoltage', render: v => fmt0(v), width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseVoltage')}</span>, dataIndex: 'CphaseVoltage', key: 'CphaseVoltage', render: v => fmt0(v), width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.aPhaseCurrent')}</span>, dataIndex: 'AphaseCurrent', key: 'AphaseCurrent', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.bPhaseCurrent')}</span>, dataIndex: 'BphaseCurrent', key: 'BphaseCurrent', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.cPhaseCurrent')}</span>, dataIndex: 'CphaseCurrent', key: 'CphaseCurrent', render: fmt0, width: 150, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.activePowerKw')}</span>, dataIndex: 'ActivePower', key: 'ActivePower', render: v => <span style={{ color: COLORS.solar }}>{fmt0(v)}</span>, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.reactivePowerKvar')}</span>, dataIndex: 'ReactivePower', key: 'ReactivePower', render: fmt0, width: 160, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.sysStatus')}</span>, dataIndex: 'RunStatu', key: 'RunStatu', render: v => v != null ? <Tag>{v}</Tag> : '-', width: 90 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const dashboardBmsColumns = [
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceId')}</span>, dataIndex: 'device_info_id', key: 'device_info_id', width: 130 },
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.online')}</span>, dataIndex: 'Online', key: 'Online', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('cloud.online') : t('cloud.offline')}</Tag>, width: 80 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.soc')}</span>, dataIndex: 'SOC', key: 'SOC', render: v => <span style={{ color: COLORS.storage, fontWeight: 'bold' }}>{fmt0(v)}</span>, width: 90 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.rackVoltageV')}</span>, dataIndex: 'RackVoltage', key: 'RackVoltage', render: fmt0, width: 150 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.rackCurrentA')}</span>, dataIndex: 'RackCurrent', key: 'RackCurrent', render: fmt0, width: 150 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmCnt')}</span>, dataIndex: 'AlarmCnt', key: 'AlarmCnt', render: fmt0, width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  const dashboardEmsColumns = [
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceId')}</span>, dataIndex: 'device_info_id', key: 'device_info_id', width: 130 },
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130, },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.controlMode')}</span>, dataIndex: 'ControlMode', key: 'ControlMode', render: v => [t('cloud.stopMachine'), t('cloud.gridConnected'), t('cloud.offGrid')][v] ?? v, width: 150 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.strategyType')}</span>, dataIndex: 'StratagyType', key: 'StratagyType', render: v => fmt0(v), width: 140 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.sysStatus')}</span>, dataIndex: 'SysStatus', key: 'SysStatus', render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? t('cloud.running') : t('cloud.stopped')}</Tag>, width: 100 },
    { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordTime')}</span>, dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160, },
  ];

  // Tab配置
  const tabItems = [
    { key: 'telemetry', label: <span><DashboardOutlined /> {t('nav.telemetry')}</span>,
      children: <TelemetryTab telemetry={telemetry} dashboardData={dashboardData} pollInterval={pollInterval} setPollInterval={setPollInterval} lastUpdate={lastUpdate} onRefresh={handleRefresh} /> },
    {
      key: 'controllers', label: <span><AppstoreOutlined /> {t('nav.controllers')}</span>,
      children: <ControllersTab controllerStats={controllerStats} controllers={controllers} loading={loading} pageSize={pageSize} statusColor={statusColor} statusText={statusText} />
    },
    {
      key: 'sub-devices', label: <span><ThunderboltOutlined /> {t('nav.subDevice')}</span>,
      children: <SubDevicesTab subDeviceStats={subDeviceStats} subDevices={subDevices} loading={loading} statusColor={statusColor} statusText={statusText} />
    },
    {
      key: 'alarms', label: <span><AlertOutlined /> {t('nav.alarm')} {alarmStats.pending > 0 && <Badge count={alarmStats.pending} style={{ backgroundColor: '#faad14' }} />}</span>,
      children: <AlarmTab alarmStats={alarmStats} alarms={alarms} alarmsWithDef={alarmsWithDef} alarmPageSize={alarmPageSize} fetchAlarms={fetchAlarms} handleAlarmStatusChange={handleAlarmStatusChange} />
    },
    {
      key: 'config', label: <span><SettingOutlined /> {t('nav.config')}</span>,
      children: <TelecontrolTab controllers={controllers} subDevices={subDevices} api={api} lz4Compress={lz4Compress} setLz4Compress={setLz4Compress} refreshKey={refreshKey} currentUser={currentUser} />
    },
    {
      key: 'history', label: <span><HistoryOutlined /> 历史记录</span>,
      children: <HistoryTab controllers={controllers} />
    },
    {
      key: 'logs', label: <span><HistoryOutlined /> {t('log.title')}</span>,
      children: <LogsTab controlLogs={controlLogs} loading={loading} pageSize={pageSize} />
    },
  ];

  return (
    <div style={{ minHeight: '100%' }} className="cloud-platform-page">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '16px 20px' } }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <CloudOutlined style={{ fontSize: 24, color: COLORS.primary }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text }}>{t('page.title')}</div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{t('page.subtitle')}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('common.refresh')}
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 0 } }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}
          tabBarStyle={{ paddingLeft: 16, marginBottom: 0, color: COLORS.muted }}
          items={tabItems} />
      </Card>

    </div>
  );
};

export default CloudPlatformPage;
